(() => {
  if (customElements.get('elixir-variant-image-link')) return;

  const normalize = (value) => String(value ?? '').normalize('NFKC').trim().toLowerCase().replace(/[–—]/g, '-').replace(/\s+/g, ' ');
  const appBlocks = ':is(.shopify-block, .shopify-app-block)';
  const controls = 'select, option, input, [data-variant-id], [data-value], [role="option"], [role="radio"], [role="combobox"], button[aria-haspopup="listbox"]';

  class ElixirVariantMediaSync extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.productInfo = this.closest('product-info');
      if (!this.productInfo) {
        this.debug({ state: 'product-info-missing' });
        return;
      }
      try {
        this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      } catch (error) {
        this.debug({ state: 'variant-data-error', error: error.message });
        return;
      }
      this.debug({
        version: 'image-link-2',
        state: 'ready',
        variants: this.variants.length,
        photos: this.variants.filter((variant) => variant.media?.preview_image?.src).length
      });
      this.sectionId = this.productInfo.dataset.section;
      this.variantById = new Map(this.variants.map((variant) => [String(variant.id), variant]));
      this.variantMediaIds = new Set(this.variants.map((variant) => variant.media?.id).filter(Boolean).map(String));
      this.controller = new AbortController();
      this.masterVariantId = this.masterInput?.value;
      const options = { capture: true, signal: this.controller.signal };
      document.addEventListener('change', (event) => this.onSelection(event, false), options);
      document.addEventListener('input', (event) => this.onSelection(event, false), options);
      document.addEventListener('click', (event) => this.onSelection(event, true), options);
      this.observer = new MutationObserver((records) => {
        let appChanged = false;
        for (const record of records) {
          const target = record.target instanceof Element ? record.target : record.target.parentElement;
          if (!target) continue;
          if (target === this.masterInput) {
            this.checkMasterInput();
          } else if (target.closest(appBlocks)) {
            appChanged = true;
            if (record.type === 'attributes' && (
              target.getAttribute('aria-selected') === 'false' ||
              target.getAttribute('aria-checked') === 'false' ||
              target.getAttribute('data-state') === 'unchecked'
            )) continue;
            const control = target.closest(controls);
            const variant = control && this.resolveControl(control);
            if (variant) this.schedule(variant);
          }
        }
        this.checkMasterInput();
        if (appChanged && !this.pendingVariant) this.checkAppSelection();
      });
      this.observer.observe(this.productInfo.querySelector('[id^="ProductInfo-"]') || this.productInfo, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['value', 'checked', 'selected', 'aria-selected', 'aria-checked', 'data-state', 'data-variant-id', 'data-value'],
      });
      this.checkAppSelection();
    }

    debug(update) {
      const output = this.productInfo?.querySelector('[data-elixir-variant-debug-output]') ||
        this.parentElement?.querySelector('[data-elixir-variant-debug-output]');
      if (!output) return;
      this.debugState = { ...this.debugState, ...update };
      output.textContent = JSON.stringify(this.debugState, null, 2);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.selectionFrame);
      clearTimeout(this.selectionTimer);
      this.controller = null;
    }

    get masterInput() {
      return this.productInfo.querySelector('product-form form input[name="id"]');
    }

    resolveValue(value) {
      const id = String(value ?? '').replace(/^gid:\/\/shopify\/ProductVariant\//, '');
      if (this.variantById.has(id)) return this.variantById.get(id);
      const text = normalize(value);
      if (!text) return null;
      const exact = this.variants.filter((variant) =>
        normalize(variant.title) === text ||
        (variant.options?.length === 1 && normalize(variant.options[0]) === text)
      );
      if (exact.length === 1) return exact[0];
      const size = text.match(/^(\d+(?:[.,]\d+)?)\s*ml\b/);
      if (!size) return null;
      const matches = this.variants.filter((variant) => {
        if (variant.options?.length !== 1) return false;
        const candidate = normalize(variant.options[0]).match(/^(\d+(?:[.,]\d+)?)\s*ml\b/);
        return candidate && Number(candidate[1].replace(',', '.')) === Number(size[1].replace(',', '.'));
      });
      return matches.length === 1 ? matches[0] : null;
    }

    resolveControl(control) {
      if (control.matches('input[type="number"], input[type="checkbox"]')) return null;
      if (control.matches('input[type="radio"]') && !control.checked) return null;
      const selected = control.selectedOptions?.[0];
      const candidates = [
        selected?.getAttribute('data-variant-id'),
        control.getAttribute('data-variant-id'),
        control.value,
        selected?.value,
        control.getAttribute('data-value'),
        selected?.textContent,
      ];
      if (control.matches('option, [role="option"], [role="radio"], [role="combobox"], [data-variant-id], [data-value], button[aria-haspopup="listbox"]')) {
        candidates.push(control.textContent);
      }
      for (const value of candidates) {
        const variant = this.resolveValue(value);
        if (variant) return variant;
      }
      return null;
    }

    ownsTarget(target) {
      if (this.productInfo.contains(target)) {
        return !target.closest('variant-selects') && (
          Boolean(target.closest(appBlocks)) ||
          target === this.masterInput
        );
      }
      const popup = target.closest('[role="listbox"]');
      if (!popup?.id) return false;
      return Array.from(this.productInfo.querySelectorAll(appBlocks + ' [aria-controls]'))
        .some((trigger) => trigger.getAttribute('aria-controls')?.split(/\s+/).includes(popup.id));
    }

    onSelection(event, afterClick) {
      const target = event.target instanceof Element ? event.target : event.target.parentElement;
      if (!target) return;
      const owned = this.ownsTarget(target);
      const candidate = target.closest(controls);
      if (this.productInfo.contains(target) || candidate?.matches('[role="option"]')) {
        const choice = candidate?.matches('select, option, [role="option"], [role="combobox"], [data-variant-id]') ?
          candidate.value || candidate.textContent?.trim().slice(0,100) : '';
        this.debug({
          event: event.type,
          target: target.tagName.toLowerCase() + (target.className ? '.' + String(target.className).trim().replace(/\s+/g,'.').slice(0,100) : ''),
          handled: owned,
          choice
        });
      }
      if (!owned) return;
      const update = () => {
        if (!this.isConnected) return;
        const label = target.closest('label');
        const control = label?.control || target.closest(controls);
        const variant = control && this.resolveControl(control);
        if (variant) {
          this.debug({ matched: variant.title });
          if (control === this.masterInput) this.masterVariantId = control.value;
          this.schedule(variant);
        } else {
          this.checkMasterInput();
        }
      };
      cancelAnimationFrame(this.selectionFrame);
      clearTimeout(this.selectionTimer);
      if (!afterClick) update();
      this.selectionFrame = requestAnimationFrame(update);
      this.selectionTimer = setTimeout(() => {
        if (this.isConnected) this.checkMasterInput();
      }, 160);
    }

    checkAppSelection() {
      const selectedControls = this.productInfo.querySelectorAll(
        appBlocks + ' select, ' + appBlocks + ' input[type="radio"]:checked, ' + appBlocks + ' [role="combobox"]'
      );
      for (const control of selectedControls) {
        const variant = this.resolveControl(control);
        if (variant) {
          this.schedule(variant);
          return;
        }
      }
    }

    checkMasterInput() {
      const input = this.masterInput;
      if (!input || input.value === this.masterVariantId) return;
      this.masterVariantId = input.value;
      if (this.productInfo.querySelector('variant-selects')) return;
      const variant = this.resolveValue(input.value);
      if (variant) this.schedule(variant);
    }

    schedule(variant) {
      if (!variant.media?.preview_image?.src) {
        this.debug({ state: 'assigned-photo-missing', matched: variant.title });
        return;
      }
      this.pendingVariant = variant;
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        const gallery = this.productInfo.querySelector('media-gallery');
        if (typeof gallery?.setActiveMedia !== 'function') {
          this.debug({ state: 'waiting-for-gallery', galleryPresent: Boolean(gallery) });
          customElements.whenDefined('media-gallery').then(() => {
            if (this.isConnected && this.pendingVariant) this.schedule(this.pendingVariant);
          });
          return;
        }
        try {
          this.showVariant(this.pendingVariant, gallery);
        } catch (error) {
          this.debug({ state: 'gallery-error', error: error.message });
        }
      });
    }

    showVariant(variant, gallery) {
      const media = variant.media;
      if (!media || media.media_type !== 'image') return;
      const mediaId = this.sectionId + '-' + media.id;
      const viewer = gallery.querySelector('[id^="GalleryViewer"]');
      if (!viewer) {
        this.debug({ state: 'gallery-viewer-missing' });
        return;
      }
      const slides = Array.from(viewer.querySelectorAll('li[data-media-id]'));
      let slide = slides.find((item) => item.dataset.mediaId === mediaId);
      if (!slide) {
        slide = slides.find((item) => this.variantMediaIds.has(
          item.dataset.mediaId.slice(this.sectionId.length + 1)
        ) && item.querySelector('.product__modal-opener--image img'));
        let cloned = false;
        if (!slide) {
          const original = slides.find((item) => item.querySelector('.product__modal-opener--image img'));
          if (!original) {
            this.debug({ state: 'gallery-image-slide-missing', slides: slides.length });
            return;
          }
          slide = original.cloneNode(true);
          cloned = true;
        }
        const img = slide.querySelector('.product__media img');
        if (!img) {
          this.debug({ state: 'gallery-image-element-missing' });
          return;
        }
        const preview = media.preview_image;
        const source = new URL(preview.src, window.location.href);
        const width = Number(preview.width);
        const height = Number(preview.height);
        if (!width || !height || !['https:', 'http:'].includes(source.protocol)) {
          this.debug({ state: 'photo-data-invalid', width, height });
          return;
        }
        const imageUrl = (size) => {
          const url = new URL(source);
          url.searchParams.set('width', size);
          return url.href;
        };
        const maxWidth = Math.min(width, 1946);
        const widths = [246, 493, 600, 713, 823, 990, 1100, 1206, 1346, 1426, 1646, maxWidth]
          .filter((size, index, all) => size <= maxWidth && all.indexOf(size) === index)
          .sort((a, b) => a - b);
        slide.id = 'Slide-' + mediaId;
        slide.dataset.mediaId = mediaId;
        slide.querySelectorAll('button[data-media-id]').forEach((button) => {
          button.dataset.mediaId = String(media.id);
        });
        const container = slide.querySelector('.product-media-container');
        container?.style.setProperty('--ratio', String(media.aspect_ratio || width / height));
        container?.style.setProperty('--preview-ratio', String(width / height));
        img.alt = media.alt || preview.alt || '';
        img.width = width;
        img.height = height;
        img.loading = 'eager';
        img.fetchPriority = 'high';
        img.srcset = widths.map((size) => imageUrl(size) + ' ' + size + 'w').join(', ');
        img.src = imageUrl(maxWidth);
        if (cloned) viewer.querySelector('ul').prepend(slide);
      }
      if (slide.classList.contains('is-active') && this.activeMediaId === mediaId) return;
      this.activeMediaId = mediaId;
      gallery.setActiveMedia(mediaId, true);
      this.debug({ state: 'image-updated', matched: variant.title, mediaId: String(media.id), image: slide.querySelector('.product__media img')?.getAttribute('src')?.split('/').pop()?.split('?')[0] });
    }
  }

  customElements.define('elixir-variant-image-link', ElixirVariantMediaSync);
})();
